import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { RenFaireService } from './ren-faire.service';
import { FaireItem, FaireItemCategory, FAIRE_CATEGORIES } from './ren-faire.model';

@Component({
    selector: 'app-ren-faire',
    templateUrl: './ren-faire.component.html',
    styleUrls: ['./ren-faire.component.scss'],
    standalone: false
})
export class RenFaireComponent implements OnInit, OnDestroy {
  items: FaireItem[] = [];
  groupedDays: { date: string; label: string; items: FaireItem[] }[] = [];
  undatedItems: FaireItem[] = [];
  collapsedDays: Set<string> = new Set();

  showItemModal = false;
  selectedItem: FaireItem | null = null;

  showFormModal = false;
  editingItemId: string | null = null;
  newItem: Partial<FaireItem> = {};
  formError = '';

  showHuzzah = false;

  draggingItem: FaireItem | null = null;
  dragOverDate: string | null = null;
  dragOverUnscheduled = false;

  categories = FAIRE_CATEGORIES;

  // ── Touch drag state ──────────────────────────────────────────────────────
  private ghost: HTMLElement | null = null;
  private touchDragItem: FaireItem | null = null;
  private touchDragStarted = false;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchGhostOffsetX = 0;
  private touchGhostOffsetY = 0;
  private touchTargetEl: HTMLElement | null = null;
  private boundTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundTouchEnd: ((e: TouchEvent) => void) | null = null;

  private itemsSub?: Subscription;

  constructor(private renFaireService: RenFaireService) {}

  get showRightPanel(): boolean {
    return !!this.selectedItem || this.undatedItems.length > 0 || !!(this.draggingItem?.date);
  }

  ngOnInit() {
    this.itemsSub = this.renFaireService.getItems().subscribe(items => {
      this.items = items.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
      this.groupedDays = this.computeGroupedDays();
      this.undatedItems = this.items.filter(item => !item.date);

      if (this.selectedItem) {
        this.selectedItem = this.items.find(i => i.id === this.selectedItem!.id) || null;
      }
    });
    this.resetForm();
  }

  ngOnDestroy() {
    this.itemsSub?.unsubscribe();
    this._removeTouchListeners();
    if (this.ghost) {
      document.body.removeChild(this.ghost);
      this.ghost = null;
    }
  }

  private computeGroupedDays(): { date: string; label: string; items: FaireItem[] }[] {
    const dayMap = new Map<string, FaireItem[]>();
    this.items.filter(item => !!item.date).forEach(item => {
      if (!dayMap.has(item.date!)) dayMap.set(item.date!, []);
      dayMap.get(item.date!)!.push(item);
    });
    return Array.from(dayMap.entries()).map(([date, dayItems]) => ({
      date,
      label: new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      }),
      items: dayItems
    }));
  }

  toggleDay(date: string) {
    if (this.collapsedDays.has(date)) {
      this.collapsedDays.delete(date);
    } else {
      this.collapsedDays.add(date);
    }
  }

  isDayCollapsed(date: string): boolean {
    return this.collapsedDays.has(date);
  }

  getCategoryConfig(category: FaireItemCategory | undefined) {
    return FAIRE_CATEGORIES.find(c => c.value === category) || null;
  }

  formatTime(time: string | undefined): string {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return time;
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
  }

  openItemModal(item: FaireItem) {
    this.selectedItem = item;
    this.showItemModal = true;
  }

  closeItemModal() {
    this.showItemModal = false;
    this.selectedItem = null;
  }

  openFormModal() {
    this.showFormModal = true;
  }

  openFormModalForDay(date: string) {
    this.resetForm();
    this.newItem = { date };
    this.showFormModal = true;
  }

  openEditModal(item: FaireItem) {
    this.closeItemModal();
    this.editingItemId = item.id;
    this.newItem = { ...item };
    this.formError = '';
    this.showFormModal = true;
  }

  closeFormModal() {
    this.showFormModal = false;
    this.resetForm();
  }

  resetForm() {
    this.editingItemId = null;
    this.newItem = {};
    this.formError = '';
  }

  saveItem() {
    this.formError = '';
    if (!this.newItem.name) {
      this.formError = 'Hark! A proclamation (name) is required.';
      return;
    }
    if (this.newItem.link && !/^https?:\/\//i.test(this.newItem.link)) {
      this.formError = 'Prithee, the link must begin with http:// or https://';
      return;
    }

    const item: FaireItem = {
      id: this.editingItemId || uuidv4(),
      name: this.newItem.name,
      ...(this.newItem.date && { date: this.newItem.date }),
      ...(this.newItem.time && { time: this.newItem.time }),
      ...(this.newItem.category && { category: this.newItem.category }),
      ...(this.newItem.link && { link: this.newItem.link }),
      ...(this.newItem.notes && { notes: this.newItem.notes }),
      ...(this.newItem.location && { location: this.newItem.location }),
      ...(this.newItem.cost != null && { cost: this.newItem.cost }),
    };

    const savePromise = this.editingItemId
      ? this.renFaireService.updateItem(item)
      : this.renFaireService.addItem(item);

    savePromise.then(() => {
      this.closeFormModal();
      this.showHuzzah = true;
      setTimeout(() => { this.showHuzzah = false; }, 1500);
    }).catch(() => {
      this.formError = 'Forsooth! The scribe hath failed. Prithee try again.';
    });
  }

  deleteItem(item: FaireItem) {
    if (confirm(`Banish "${item.name}" from the chronicle? This cannot be undone.`)) {
      this.renFaireService.deleteItem(item.id).then(() => {
        this.closeItemModal();
      }).catch(() => {
        alert('The banishment hath failed. Prithee try again.');
      });
    }
  }

  // ── Mouse drag ────────────────────────────────────────────────────────────

  onDragStart(item: FaireItem) {
    this.draggingItem = item;
  }

  onDragEnd() {
    this.draggingItem = null;
    this.dragOverDate = null;
    this.dragOverUnscheduled = false;
  }

  onDragOver(event: DragEvent, date: string) {
    event.preventDefault();
    this.dragOverDate = date;
  }

  onDrop(event: DragEvent, date: string) {
    event.preventDefault();
    if (this.draggingItem) {
      const updated: FaireItem = { ...this.draggingItem, date };
      this.renFaireService.updateItem(updated).catch(() => {
        alert('The quest could not be scheduled. Prithee try again.');
      });
    }
    this.draggingItem = null;
    this.dragOverDate = null;
  }

  onDragOverUnscheduled(event: DragEvent) {
    event.preventDefault();
    this.dragOverUnscheduled = true;
  }

  onDropUnscheduled(event: DragEvent) {
    event.preventDefault();
    if (this.draggingItem && this.draggingItem.date) {
      const { date: _removed, ...rest } = this.draggingItem;
      this.renFaireService.updateItem(rest as FaireItem).catch(() => {
        alert('The quest could not be unscheduled. Prithee try again.');
      });
    }
    this.draggingItem = null;
    this.dragOverUnscheduled = false;
  }

  // ── Touch drag ────────────────────────────────────────────────────────────

  onTouchStart(event: TouchEvent, item: FaireItem): void {
    if (this.showFormModal || this.showItemModal) return;

    const touch = event.touches[0];
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    this.touchDragItem = item;
    this.touchDragStarted = false;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchGhostOffsetX = touch.clientX - rect.left;
    this.touchGhostOffsetY = touch.clientY - rect.top;
    this.touchTargetEl = target;

    this.boundTouchMove = this._onTouchMove.bind(this);
    this.boundTouchEnd = this._onTouchEnd.bind(this);
    document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    document.addEventListener('touchend', this.boundTouchEnd);
    document.addEventListener('touchcancel', this.boundTouchEnd);
  }

  private _onTouchMove(event: TouchEvent): void {
    if (!this.touchDragItem) return;

    const touch = event.touches[0];
    const dx = touch.clientX - this.touchStartX;
    const dy = touch.clientY - this.touchStartY;

    // Wait until finger moves 8px before committing to a drag
    if (!this.touchDragStarted) {
      if (Math.hypot(dx, dy) < 8) return;
      this.touchDragStarted = true;
      this.draggingItem = this.touchDragItem;

      // Build ghost from the card element
      const rect = this.touchTargetEl!.getBoundingClientRect();
      this.ghost = this.touchTargetEl!.cloneNode(true) as HTMLElement;
      Object.assign(this.ghost.style, {
        position: 'fixed',
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        pointerEvents: 'none',
        zIndex: '9999',
        opacity: '0.92',
        transform: 'scale(1.04) rotate(1.5deg)',
        boxShadow: '0 10px 28px rgba(61, 90, 46, 0.35)',
        borderRadius: '8px',
        transition: 'none',
      });
      document.body.appendChild(this.ghost);
    }

    // Prevent page scroll while dragging
    event.preventDefault();

    this.ghost!.style.left = `${touch.clientX - this.touchGhostOffsetX}px`;
    this.ghost!.style.top  = `${touch.clientY - this.touchGhostOffsetY}px`;

    // Detect which drop zone the finger is over.
    // The ghost has pointer-events:none so it's invisible to elementFromPoint.
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    this.dragOverDate = null;
    this.dragOverUnscheduled = false;

    if (el) {
      const dayGroup = el.closest('[data-drop-date]');
      const unscheduledEl = el.closest('[data-drop-unscheduled]');
      if (dayGroup) {
        this.dragOverDate = dayGroup.getAttribute('data-drop-date');
      } else if (unscheduledEl) {
        this.dragOverUnscheduled = true;
      }
    }
  }

  private _onTouchEnd(_event: TouchEvent): void {
    this._removeTouchListeners();

    if (this.ghost) {
      document.body.removeChild(this.ghost);
      this.ghost = null;
    }

    const item = this.touchDragItem;
    const wasDrag = this.touchDragStarted;

    this.touchDragItem = null;
    this.touchTargetEl = null;
    this.touchDragStarted = false;
    this.draggingItem = null;

    if (!item || !wasDrag) {
      // Tap, not drag — let the click event through normally
      this.dragOverDate = null;
      this.dragOverUnscheduled = false;
      return;
    }

    if (this.dragOverDate) {
      const date = this.dragOverDate;
      this.dragOverDate = null;
      this.renFaireService.updateItem({ ...item, date }).catch(() => {
        alert('The quest could not be scheduled. Prithee try again.');
      });
    } else if (this.dragOverUnscheduled && item.date) {
      this.dragOverUnscheduled = false;
      const { date: _removed, ...rest } = item;
      this.renFaireService.updateItem(rest as FaireItem).catch(() => {
        alert('The quest could not be unscheduled. Prithee try again.');
      });
    } else {
      this.dragOverDate = null;
      this.dragOverUnscheduled = false;
    }
  }

  private _removeTouchListeners(): void {
    if (this.boundTouchMove) {
      document.removeEventListener('touchmove', this.boundTouchMove);
      this.boundTouchMove = null;
    }
    if (this.boundTouchEnd) {
      document.removeEventListener('touchend', this.boundTouchEnd);
      document.removeEventListener('touchcancel', this.boundTouchEnd);
      this.boundTouchEnd = null;
    }
  }
}
